import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaFlask,
  FaSyncAlt,
  FaCog,
  FaExternalLinkAlt,
  FaSpinner,
} from 'react-icons/fa';

import { LocalStorageService } from '../../../services/cookieService';
import { startPollingTaskStatus } from '../../../store/slices/integrationsSlice';

import './IntegrationCard.css';

// ─────────────────────────────────────────────────────────────
// Microsoft Logo
// ─────────────────────────────────────────────────────────────
const MicrosoftLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24">
    <rect x="1" y="1" width="10" height="10" fill="#F25022" rx="1" />
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00" rx="1" />
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF" rx="1" />
    <rect x="13" y="13" width="10" height="10" fill="#FFB900" rx="1" />
  </svg>
);

// ─────────────────────────────────────────────────────────────
// Status Icon
// ─────────────────────────────────────────────────────────────
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'connected':
      return <FaCheckCircle size={20} className="ic-status-icon icon-connected" />;
    case 'mock':
      return <FaFlask size={20} className="ic-status-icon icon-mock" />;
    default:
      return <FaTimesCircle size={20} className="ic-status-icon icon-disconnected" />;
  }
};

// ─────────────────────────────────────────────────────────────
// Platform Icon
// ─────────────────────────────────────────────────────────────
const PlatformIcon = ({ platform, status }) => {
  if (platform === 'microsoft_entra_id') return <MicrosoftLogo />;
  return <StatusIcon status={status} />;
};

const SYNC_RESPONSE_TYPE = {
  RATE_LIMITED: 'RATE_LIMITED',   // Response 1 — show message 5s, no polling
  SYNC_STARTED: 'SYNC_STARTED',   // Response 2 — start polling, show until SUCCESS/FAILURE
  ALREADY_RUNNING: 'ALREADY_RUNNING', // Response 3 — show message 5s, no polling
};

const detectSyncResponseType = (message = '') => {
  const lower = message.toLowerCase();

  if (lower.includes('syncing data')) {
    return SYNC_RESPONSE_TYPE.SYNC_STARTED;
  }

  if (lower.includes('already running')) {
    return SYNC_RESPONSE_TYPE.ALREADY_RUNNING;
  }

  // "triggered recently" / rate-limit messages
  if (lower.includes('triggered recently') || lower.includes('minute')) {
    return SYNC_RESPONSE_TYPE.RATE_LIMITED;
  }

  // Safe fallback — treat unknown as non-polling
  return SYNC_RESPONSE_TYPE.RATE_LIMITED;
};

const IntegrationCard = ({ integration, onSync, onTestConnection }) => {
  const dispatch = useDispatch();

  const activeTask = useSelector(
    (state) => state.integrations.activeTasks[integration?.id]
  );

  const [message, setMessage] = useState('');
  const [showMessages, setShowMessages] = useState(null);
  const [messageType, setMessageType] = useState('info');   // 'info' | 'success' | 'error'
  const [cardSuccessState, setCardSuccessState] = useState(false);
  const [keepMsg, setKeepMsg] = useState(false);
  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  const [isPolling, setIsPolling] = useState(false);    // true only while polling is active

  
  const pollingIntervalRef  = useRef(null); // holds setInterval id
  const prevTaskStatusRef   = useRef(null); // tracks last seen taskStatus
  const autoHideTimerRef    = useRef(null); // holds auto-hide setTimeout id
  const isPollingActiveRef  = useRef(false);// mirrors isPolling without stale closure

  const taskStatus = activeTask?.status;

  const isSyncing = isPolling || taskStatus === 'PENDING';

  const isActive = integration?.status === 'connected' || integration?.status === 'mock';
  const isComingSoon = integration?.comingSoon;
  const isSyncButtonDisabled = isSyncing || apiCallInProgress;

  const showBanner = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setShowMessages(true);
  };

  const scheduleAutoHide = (ms) => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
    }

    autoHideTimerRef.current = setTimeout(() => {
      setShowMessages(false);
      autoHideTimerRef.current = null;
    }, ms);
  };

  const clearLocalStorageTask = () => {
    LocalStorageService.removeTaskStatus(integration?.id);
    LocalStorageService.removeTaskId(integration?.id);
    LocalStorageService.removeTaskMessage(integration?.id);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingActiveRef.current = false;
    setIsPolling(false);
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) return;

    isPollingActiveRef.current = true;
    setIsPolling(true);

    const message = localStorage.getItem(`message_${integration?.id}`)
    showBanner(message, "pending")
    setKeepMsg(true)

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const taskId = LocalStorageService.getTaskId(integration?.id);
        const message = localStorage.getItem(`message_${integration?.id}`);
        if (!taskId) {
          stopPolling();
          clearLocalStorageTask();
          return;
        }
        
        const result = await dispatch(
          startPollingTaskStatus({
            integrationId: integration?.id,
            taskId,
          })
        ).unwrap();
        
        if (result?.payload?.status === 'SUCCESS') {
          // console.log("success")
          LocalStorageService.setTaskStatus(result?.status, integration?.id)
          stopPolling();
          setShowMessages(true)
          showBanner("Sync successfully completed.", "success")
          scheduleAutoHide(4000);
          setKeepMsg(false)
          LocalStorageService.removeAll(integration?.id)
        }

        else if (result?.payload?.status === 'FAILURE') {
          stopPolling();
          showBanner("Sync failed, try again.", "error")
          LocalStorageService.removeAll(integration?.id)
        }
      } catch (error) {
        console.error('Polling error:', error);
        stopPolling();
        clearLocalStorageTask();
        showBanner(error?.message || 'Sync Failed. Please try again.', 'error');
      }
    }, 10000);
  };

  const handleSync = async () => {
    try {
      setApiCallInProgress(true);

      const savedStatus = LocalStorageService.getTaskStatus(integration?.id);
      const savedTaskId = LocalStorageService.getTaskId(integration?.id);

      if (savedStatus === 'PENDING' && savedTaskId) {
        const savedMessage = LocalStorageService.getTaskMessage(integration?.id) || 'A sync is already in progress. Please wait...';
        showBanner(savedMessage, 'info');
        startPolling();
        return;
      }

      // ── Fire the sync API call ──────────────────────────────
      const result = await onSync(integration?.id);
      
      if (!result) {
        showBanner('Sync request failed. No response received.', 'error');
        return;
      }

      const responseType = detectSyncResponseType(result?.message);

      // ── Response 2: Sync actually started → poll ────────────
      if (responseType === SYNC_RESPONSE_TYPE.SYNC_STARTED) {
        if (result?.task_id) {
          LocalStorageService.setTaskId(result.task_id, integration?.id);
          LocalStorageService.setTaskStatus('PENDING', integration?.id);
          LocalStorageService.setTaskMessage(
            result?.message || 'Syncing data. Please wait...',
            integration?.id
          );
          setKeepMsg(true)
        }

        showBanner(result?.message || 'Sync started. Please wait...', 'info');
        startPolling();
        // Message stays visible until polling resolves (SUCCESS/FAILURE effect handles hide)
        return;
      }

      // ── Response 1 or 3: Informational — show for 5s, no polling ──
      else if (
        responseType === SYNC_RESPONSE_TYPE.RATE_LIMITED ||
        responseType === SYNC_RESPONSE_TYPE.ALREADY_RUNNING
      ) {
        showBanner(result?.message || 'Unable to start sync.', 'info');
        setShowMessages(true)
        setKeepMsg(result?.message)
        scheduleAutoHide(4000);
        return;
      }
    } catch (error) {
      console.error('Sync error:', error);
      showBanner(error?.message || 'Sync failed. Please try again.', 'error');
    } finally {
      setApiCallInProgress(false);
    }
  };

  useEffect(()=>{
    const taskStatus = LocalStorageService.getTaskStatus(integration?.id)
    const taskId = LocalStorageService.getTaskId(integration?.id)

    if(taskStatus === "PENDING"){
      startPolling()
    }
  }, [])

  // ───────────────────────────────────────────────────────────
  // UI Helpers
  // ───────────────────────────────────────────────────────────
  const getMessageIcon = () => {
    if (messageType === 'success') return <FaCheckCircle className="icon" />;
    if (messageType === 'error')   return <FaExclamationCircle className="icon" />;
    // 'info' — show spinner while actively syncing/polling, otherwise spinner too
    return;
  };

  const getSyncButtonTooltip = () => {
    if (isSyncing)        return 'Sync in progress';
    if (apiCallInProgress) return 'Please wait...';
    return 'Sync Now';
  };

  // ───────────────────────────────────────────────────────────
  // Card CSS classes
  // ───────────────────────────────────────────────────────────
  const cardClass = [
    'integration-card',
    `card-${integration?.status}`,
    isComingSoon    ? 'card-coming-soon' : '',
    cardSuccessState ? 'success-state'   : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, []);

  return (
    <div className={cardClass}>
      {/* ── Header ── */}
      <div className="ic-header">
        <div className="ic-header-left">
          <div className="ic-icon-box">
            <PlatformIcon
              platform={integration?.platform}
              status={integration?.status}
            />
          </div>

          <div className="ic-info">
            <h3>{integration?.name}</h3>
            <p>{integration?.description}</p>
            <span className="ic-category-tag">{integration?.category}</span>
          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="ic-header-actions">
          {isActive && (
            <>
              <button
                className="ic-action-btn"
                onClick={handleSync}
                disabled={isSyncButtonDisabled}
                title={getSyncButtonTooltip()}
                aria-label={getSyncButtonTooltip()}
              >
                <FaSyncAlt
                  size={14}
                  className={isSyncing ? 'icon-spinning' : ''}
                />
              </button>

              {/* <button
                className="ic-action-btn"
                onClick={() => onTestConnection(integration?.id)}
                disabled={isSyncButtonDisabled}
                title="Test Connection"
                aria-label="Test Connection"
              >
                <FaCog size={14} />
              </button> */}
            </>
          )}
        </div>
      </div>

      {/* ── Status Grid ── */}
      {isActive && (
        <div className="ic-stats-grid">
          <div>
            <p className="ic-stat-label">Status</p>
            <p className={`ic-stat-value val-${integration?.status}`}>
              {integration?.status}
            </p>
          </div>
        </div>
      )}

      {/* ── Connect Button ── */}
      {!isActive && !isComingSoon && (
        <div className="ic-bottom-section">
          <button className="ic-btn-connect">
            <FaExternalLinkAlt size={12} />
            Connect
          </button>
        </div>
      )}

      {/* ── Coming Soon ── */}
      {isComingSoon && (
        <div className="ic-bottom-section">
          <button className="ic-btn-connect" disabled>
            Integrate
          </button>
        </div>
      )}

      {/* ── Message Banner ── */}
      {showMessages && keepMsg && (
        <div
          className={`ic-test-result ${
            messageType === 'success'
              ? 'test-success'
              : messageType === 'error'
              ? 'test-failure'
              : 'test-pending'
          }`}
          role="alert"
          aria-live="polite"
        >
          <div className="message-content">
            {getMessageIcon()}
            <span>{message}</span>
          </div>
        </div>
      )}
      {showMessages && !keepMsg && (
        <div
          className={`ic-test-result ${
            messageType === 'success'
              ? 'test-success'
              : messageType === 'error'
              ? 'test-failure'
              : 'test-pending'
          }`}
          role="alert"
          aria-live="polite"
        >
          <div className="message-content">
            {getMessageIcon()}
            <span>{message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationCard;